import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Building2, TrendingUp, TrendingDown, MessageSquare, X, ChevronRight, ThumbsUp, ThumbsDown, Minus, MapPin, Calendar, Filter, Store } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { KPICard } from '../components/ui/KPICard';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
const countries = [{
  id: 'global',
  name: 'Global',
  icon: Building2
}, {
  id: 'deutschland',
  name: 'Deutschland'
}, {
  id: 'frankreich',
  name: 'Frankreich'
}, {
  id: 'spanien',
  name: 'Spanien'
}, {
  id: 'italien',
  name: 'Italien'
}, {
  id: 'grossbritannien',
  name: 'Großbritannien'
}, {
  id: 'polen',
  name: 'Polen'
}, {
  id: 'niederlande',
  name: 'Niederlande'
}];
const countryData: Record<string, {
  positive: number;
  neutral: number;
  negative: number;
  total: number;
  avgRating: number;
  trend: number;
}> = {
  global: {
    positive: 10800,
    neutral: 4050,
    negative: 1950,
    total: 16800,
    avgRating: 4.0,
    trend: 0.12
  },
  deutschland: {
    positive: 1720,
    neutral: 560,
    negative: 230,
    total: 2510,
    avgRating: 4.2,
    trend: 0.18
  },
  frankreich: {
    positive: 1850,
    neutral: 680,
    negative: 420,
    total: 2950,
    avgRating: 3.9,
    trend: 0.06
  },
  spanien: {
    positive: 2100,
    neutral: 850,
    negative: 510,
    total: 3460,
    avgRating: 3.8,
    trend: -0.03
  },
  italien: {
    positive: 1450,
    neutral: 500,
    negative: 200,
    total: 2150,
    avgRating: 4.1,
    trend: 0.15
  },
  grossbritannien: {
    positive: 1380,
    neutral: 470,
    negative: 250,
    total: 2100,
    avgRating: 4.0,
    trend: 0.1
  },
  polen: {
    positive: 1520,
    neutral: 550,
    negative: 260,
    total: 2330,
    avgRating: 4.1,
    trend: 0.16
  },
  niederlande: {
    positive: 1020,
    neutral: 420,
    negative: 150,
    total: 1590,
    avgRating: 4.3,
    trend: 0.2
  }
};
const topicClusters: Record<string, Array<{
  name: string;
  size: number;
  sentiment: 'positive' | 'neutral' | 'negative';
  comments: Array<{
    text: string;
    market: string;
    date: string;
    rating: number;
  }>;
}>> = {
  global: [{
    name: 'Günstige Preise',
    size: 4500,
    sentiment: 'positive',
    comments: [{
      text: 'Unschlagbare Preise, komme immer wieder gerne!',
      market: 'LIDL Hamburg',
      date: '2024-01-28',
      rating: 5
    }, {
      text: 'Bestes Preis-Leistungs-Verhältnis in der Umgebung.',
      market: 'LIDL München-Schwabing',
      date: '2024-01-27',
      rating: 5
    }, {
      text: 'Für den Preis bekommt man hier erstaunlich gute Qualität.',
      market: 'LIDL Frankfurt-Nordend',
      date: '2024-01-26',
      rating: 5
    }, {
      text: 'Discounter-Preise mit Supermarkt-Qualität.',
      market: 'LIDL Kiel',
      date: '2024-01-25',
      rating: 5
    }, {
      text: 'Spart man wirklich im Vergleich zu anderen Märkten.',
      market: 'LIDL Freiburg',
      date: '2024-01-24',
      rating: 4
    }]
  }, {
    name: 'Backwaren',
    size: 3200,
    sentiment: 'positive',
    comments: [{
      text: 'Die Backstation ist fantastisch, immer frisch!',
      market: 'LIDL Dresden-Altstadt',
      date: '2024-01-28',
      rating: 5
    }, {
      text: 'Beste Brötchen am Morgen, super Auswahl.',
      market: 'LIDL Stuttgart-West',
      date: '2024-01-27',
      rating: 5
    }, {
      text: 'Croissants wie vom Bäcker, top!',
      market: 'LIDL Hannover-List',
      date: '2024-01-26',
      rating: 4
    }]
  }, {
    name: 'Wartezeiten',
    size: 2800,
    sentiment: 'negative',
    comments: [{
      text: 'Zu wenig Kassen offen, lange Wartezeiten.',
      market: 'LIDL Berlin-Kreuzberg',
      date: '2024-01-28',
      rating: 2
    }, {
      text: 'Samstags ist es unmöglich hier einzukaufen.',
      market: 'LIDL Köln-Ehrenfeld',
      date: '2024-01-27',
      rating: 1
    }, {
      text: 'Bitte mehr Personal an den Kassen!',
      market: 'LIDL Dortmund',
      date: '2024-01-26',
      rating: 2
    }, {
      text: 'Wartezeit von 15 Minuten ist nicht akzeptabel.',
      market: 'LIDL Duisburg',
      date: '2024-01-25',
      rating: 2
    }]
  }, {
    name: 'Lidl Plus App',
    size: 2400,
    sentiment: 'positive',
    comments: [{
      text: 'Die Lidl Plus App spart richtig Geld!',
      market: 'LIDL München',
      date: '2024-01-28',
      rating: 5
    }, {
      text: 'Tolle Coupons und Rabatte über die App.',
      market: 'LIDL Frankfurt-Sachsenhausen',
      date: '2024-01-27',
      rating: 5
    }]
  }, {
    name: 'Sortiment',
    size: 2100,
    sentiment: 'neutral',
    comments: [{
      text: 'Sortiment ist okay, aber manchmal fehlen Markenprodukte.',
      market: 'LIDL Leipzig-Zentrum',
      date: '2024-01-28',
      rating: 3
    }, {
      text: 'Eigenmarken sind gut, aber mehr Auswahl wäre schön.',
      market: 'LIDL Nürnberg',
      date: '2024-01-27',
      rating: 4
    }, {
      text: 'Für den täglichen Bedarf reicht es völlig.',
      market: 'LIDL Bremen',
      date: '2024-01-26',
      rating: 3
    }]
  }, {
    name: 'Sauberkeit',
    size: 1800,
    sentiment: 'positive',
    comments: [{
      text: 'Immer sauber und ordentlich.',
      market: 'LIDL Karlsruhe',
      date: '2024-01-28',
      rating: 5
    }, {
      text: 'Gepflegte Filiale, macht Spaß hier einzukaufen.',
      market: 'LIDL Düsseldorf-Bilk',
      date: '2024-01-27',
      rating: 5
    }]
  }, {
    name: 'Parkplatz',
    size: 1500,
    sentiment: 'negative',
    comments: [{
      text: 'Parkplatzsituation ist katastrophal.',
      market: 'LIDL Berlin-Mitte',
      date: '2024-01-28',
      rating: 2
    }, {
      text: 'Zu wenig Parkplätze, besonders abends.',
      market: 'LIDL Hamburg-Altona',
      date: '2024-01-27',
      rating: 3
    }]
  }, {
    name: 'Öffnungszeiten',
    size: 1200,
    sentiment: 'neutral',
    comments: [{
      text: 'Schade dass sonntags geschlossen ist.',
      market: 'LIDL Essen',
      date: '2024-01-28',
      rating: 3
    }, {
      text: 'Öffnungszeiten könnten länger sein.',
      market: 'LIDL Bonn',
      date: '2024-01-27',
      rating: 3
    }]
  }],
  deutschland: [{
    name: 'Günstige Preise',
    size: 620,
    sentiment: 'positive',
    comments: []
  }, {
    name: 'Backwaren',
    size: 480,
    sentiment: 'positive',
    comments: []
  }, {
    name: 'Fischprodukte',
    size: 380,
    sentiment: 'positive',
    comments: []
  }, {
    name: 'Wartezeiten',
    size: 280,
    sentiment: 'negative',
    comments: []
  }, {
    name: 'Sortiment',
    size: 350,
    sentiment: 'positive',
    comments: []
  }]
};
const monthlyTrendData: Record<string, Array<{
  month: string;
  rating: number;
  reviews: number;
  positivePercent: number;
  neutralPercent: number;
  negativePercent: number;
}>> = {
  global: [{
    month: 'Feb 23',
    rating: 3.88,
    reviews: 13200,
    positivePercent: 60,
    neutralPercent: 26,
    negativePercent: 14
  }, {
    month: 'Mär 23',
    rating: 3.9,
    reviews: 13800,
    positivePercent: 61,
    neutralPercent: 25,
    negativePercent: 14
  }, {
    month: 'Apr 23',
    rating: 3.89,
    reviews: 13600,
    positivePercent: 60,
    neutralPercent: 26,
    negativePercent: 14
  }, {
    month: 'Mai 23',
    rating: 3.92,
    reviews: 14200,
    positivePercent: 62,
    neutralPercent: 25,
    negativePercent: 13
  }, {
    month: 'Jun 23',
    rating: 3.94,
    reviews: 14800,
    positivePercent: 63,
    neutralPercent: 24,
    negativePercent: 13
  }, {
    month: 'Jul 23',
    rating: 3.92,
    reviews: 14500,
    positivePercent: 62,
    neutralPercent: 25,
    negativePercent: 13
  }, {
    month: 'Aug 23',
    rating: 3.94,
    reviews: 14700,
    positivePercent: 63,
    neutralPercent: 24,
    negativePercent: 13
  }, {
    month: 'Sep 23',
    rating: 3.96,
    reviews: 15200,
    positivePercent: 63,
    neutralPercent: 24,
    negativePercent: 13
  }, {
    month: 'Okt 23',
    rating: 3.96,
    reviews: 15800,
    positivePercent: 64,
    neutralPercent: 24,
    negativePercent: 12
  }, {
    month: 'Nov 23',
    rating: 3.98,
    reviews: 16200,
    positivePercent: 64,
    neutralPercent: 24,
    negativePercent: 12
  }, {
    month: 'Dez 23',
    rating: 3.99,
    reviews: 16500,
    positivePercent: 64,
    neutralPercent: 24,
    negativePercent: 12
  }, {
    month: 'Jan 24',
    rating: 4.0,
    reviews: 16800,
    positivePercent: 65,
    neutralPercent: 23,
    negativePercent: 12
  }],
  deutschland: [{
    month: 'Feb 23',
    rating: 4.05,
    reviews: 2000,
    positivePercent: 64,
    neutralPercent: 25,
    negativePercent: 11
  }, {
    month: 'Mär 23',
    rating: 4.07,
    reviews: 2100,
    positivePercent: 65,
    neutralPercent: 24,
    negativePercent: 11
  }, {
    month: 'Apr 23',
    rating: 4.1,
    reviews: 2050,
    positivePercent: 66,
    neutralPercent: 24,
    negativePercent: 10
  }, {
    month: 'Mai 23',
    rating: 4.12,
    reviews: 2200,
    positivePercent: 67,
    neutralPercent: 23,
    negativePercent: 10
  }, {
    month: 'Jun 23',
    rating: 4.14,
    reviews: 2300,
    positivePercent: 68,
    neutralPercent: 23,
    negativePercent: 9
  }, {
    month: 'Jul 23',
    rating: 4.15,
    reviews: 2250,
    positivePercent: 68,
    neutralPercent: 23,
    negativePercent: 9
  }, {
    month: 'Aug 23',
    rating: 4.17,
    reviews: 2350,
    positivePercent: 69,
    neutralPercent: 22,
    negativePercent: 9
  }, {
    month: 'Sep 23',
    rating: 4.16,
    reviews: 2400,
    positivePercent: 68,
    neutralPercent: 23,
    negativePercent: 9
  }, {
    month: 'Okt 23',
    rating: 4.18,
    reviews: 2450,
    positivePercent: 69,
    neutralPercent: 22,
    negativePercent: 9
  }, {
    month: 'Nov 23',
    rating: 4.19,
    reviews: 2480,
    positivePercent: 69,
    neutralPercent: 22,
    negativePercent: 9
  }, {
    month: 'Dez 23',
    rating: 4.2,
    reviews: 2500,
    positivePercent: 70,
    neutralPercent: 22,
    negativePercent: 8
  }, {
    month: 'Jan 24',
    rating: 4.22,
    reviews: 2510,
    positivePercent: 70,
    neutralPercent: 21,
    negativePercent: 9
  }]
};
const timePeriods = [{
  id: '30',
  label: '30 Tage'
}, {
  id: '60',
  label: '60 Tage'
}, {
  id: '120',
  label: '120 Tage'
}, {
  id: '365',
  label: '365 Tage'
}, {
  id: 'gesamt',
  label: 'Gesamt'
}];
function applyTimeFactor(value: number, period: string, isRating: boolean = false): number {
  if (period === 'gesamt') return value;
  if (isRating) {
    const noise = period === '30' ? 0.1 : period === '60' ? -0.1 : period === '120' ? 0.05 : -0.05;
    return Math.max(1, Math.min(5, value + noise));
  }
  const factors: Record<string, number> = {
    '30': 0.08,
    '60': 0.17,
    '120': 0.33,
    '365': 1.0
  };
  return Math.max(1, Math.round(value * (factors[period] || 1)));
}
const SimpleTreemap = ({
  data,
  onClick
}: {
  data: Array<{
    name: string;
    size: number;
    sentiment: string;
  }>;
  onClick: (item: any) => void;
}) => {
  const total = data.reduce((sum, item) => sum + item.size, 0);
  const sortedData = [...data].sort((a, b) => b.size - a.size);
  const getColors = (sentiment: string) => {
    switch (sentiment) {
      case 'positive':
        return {
          bg: '#d4edda',
          border: '#639754',
          text: '#1a3d1a'
        };
      case 'negative':
        return {
          bg: '#f8d7da',
          border: '#E60A0A',
          text: '#3d0a0a'
        };
      default:
        return {
          bg: '#fff3cd',
          border: '#FFF000',
          text: '#3d3200'
        };
    }
  };
  return <div className="grid grid-cols-4 gap-2 h-full" data-id="element-512">
      {sortedData.map((item, index) => {
      const colors = getColors(item.sentiment);
      let colSpan = 1,
        rowSpan = 1;
      if (index === 0) {
        colSpan = 2;
        rowSpan = 2;
      } else if (index === 1 || index === 2) {
        colSpan = 2;
        rowSpan = 1;
      }
      return <div key={item.name} onClick={() => onClick(item)} className="rounded-lg p-3 flex flex-col justify-center items-center text-center cursor-pointer transition-opacity hover:opacity-80" style={{
        backgroundColor: colors.bg,
        border: `2px solid ${colors.border}`,
        gridColumn: `span ${colSpan}`,
        gridRow: `span ${rowSpan}`
      }} data-id="element-513">
            <span className="font-semibold text-sm leading-tight" style={{
          color: colors.text
        }} data-id="element-514">
              {item.name}
            </span>
            <span className="text-xs mt-1 opacity-70" style={{
          color: colors.text
        }} data-id="element-515">
              {item.size.toLocaleString('de-DE')}
            </span>
          </div>;
    })}
    </div>;
};
export function KonzernStimmungPage() {
  const [selectedCountry, setSelectedCountry] = useState('global');
  const [selectedPeriod, setSelectedPeriod] = useState('gesamt');
  const [selectedTopic, setSelectedTopic] = useState<(typeof topicClusters)['konzern'][0] | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedSentimentFilter, setSelectedSentimentFilter] = useState<'positive' | 'neutral' | 'negative' | null>(null);
  const rawData = countryData[selectedCountry] || countryData.global;
  const rawTopics = topicClusters[selectedCountry] || topicClusters.global;
  const rawTrend = monthlyTrendData[selectedCountry] || monthlyTrendData.global;
  const currentData = {
    ...rawData,
    positive: applyTimeFactor(rawData.positive, selectedPeriod),
    neutral: applyTimeFactor(rawData.neutral, selectedPeriod),
    negative: applyTimeFactor(rawData.negative, selectedPeriod),
    total: applyTimeFactor(rawData.total, selectedPeriod),
    avgRating: applyTimeFactor(rawData.avgRating, selectedPeriod, true)
  };
  const currentTopics = rawTopics.map(t => ({
    ...t,
    size: applyTimeFactor(t.size, selectedPeriod)
  }));
  let currentTrend = [...rawTrend];
  if (selectedPeriod === '30') currentTrend = rawTrend.slice(-1);else if (selectedPeriod === '60') currentTrend = rawTrend.slice(-2);else if (selectedPeriod === '120') currentTrend = rawTrend.slice(-4);
  const totalSentiments = currentData.positive + currentData.neutral + currentData.negative;
  const positivePercent = Math.round(currentData.positive / totalSentiments * 100);
  const neutralPercent = Math.round(currentData.neutral / totalSentiments * 100);
  const negativePercent = Math.round(currentData.negative / totalSentiments * 100);
  const handleTopicClick = (topic: any, sentiment?: 'positive' | 'neutral' | 'negative') => {
    const fullTopic = currentTopics.find(t => t.name === topic.name);
    if (fullTopic && fullTopic.comments.length > 0) {
      setSelectedTopic(fullTopic);
      setSelectedSentimentFilter(sentiment || null);
      setIsDialogOpen(true);
    }
  };
  const getFilteredComments = () => {
    if (!selectedTopic) return [];
    if (!selectedSentimentFilter) return selectedTopic.comments;
    return selectedTopic.comments.filter(c => {
      if (selectedSentimentFilter === 'positive') return c.rating >= 4;
      if (selectedSentimentFilter === 'negative') return c.rating <= 2;
      return c.rating === 3;
    });
  };
  const sentimentLabels: Record<string, string> = {
    positive: 'Positiv',
    neutral: 'Neutral',
    negative: 'Negativ'
  };
  const sentimentColors: Record<string, string> = {
    positive: '#639754',
    neutral: '#b39500',
    negative: '#E60A0A'
  };
  return <div className="p-6 space-y-6 overflow-y-auto h-[calc(100vh-64px)]" data-id="element-516">
      <Card className="!p-4" data-id="element-517">
        <div className="flex flex-col gap-4" data-id="element-518">
          <div className="flex items-center gap-4 flex-wrap" data-id="element-519">
            <div className="flex items-center gap-2 text-sm font-medium text-gray-700" data-id="element-520">
              <Filter size={16} data-id="element-521" />
              <span data-id="element-522">Land:</span>
            </div>
            <div className="flex flex-wrap gap-2" data-id="element-523">
              {countries.map(country => {
              const isSelected = selectedCountry === country.id;
              const Icon = country.icon;
              return <button key={country.id} onClick={() => setSelectedCountry(country.id)} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${isSelected ? 'bg-lidl-blue text-white shadow-md' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-524">
                    {Icon && <Icon size={14} data-id="element-525" />}
                    {country.name}
                  </button>;
            })}
            </div>
          </div>
          <div className="flex items-center gap-4 flex-wrap" data-id="element-526">
            <div className="flex items-center gap-2 text-sm font-medium text-gray-700" data-id="element-527">
              <Calendar size={16} data-id="element-528" />
              <span data-id="element-529">Zeitraum:</span>
            </div>
            <div className="flex flex-wrap gap-2" data-id="element-530">
              {timePeriods.map(period => <button key={period.id} onClick={() => setSelectedPeriod(period.id)} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${selectedPeriod === period.id ? 'bg-lidl-blue text-white shadow-md' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-531">
                  {period.label}
                </button>)}
            </div>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-532">
        <KPICard title="Durchschnittliche Bewertung" value={currentData.avgRating.toFixed(1)} icon={<TrendingUp size={18} data-id="element-534" />} data-id="element-533" />
        <KPICard title="Gesamtbewertungen" value={currentData.total.toLocaleString('de-DE')} icon={<MessageSquare size={18} data-id="element-536" />} data-id="element-535" />
        <KPICard title="Positive Resonanz" value={`${positivePercent}%`} icon={<ThumbsUp size={18} data-id="element-538" />} data-id="element-537" />
        <KPICard title="Negative Resonanz" value={`${negativePercent}%`} icon={<ThumbsDown size={18} data-id="element-540" />} data-id="element-539" />
      </div>

      <Card data-id="element-541">
        <div className="p-6" data-id="element-542">
          <h3 className="text-lg font-semibold text-gray-900 mb-1" data-id="element-543">
            Absolute Resonanz
          </h3>
          <p className="text-sm text-gray-500 mb-6" data-id="element-544">
            Verteilung der Sentiment-Analyse
          </p>
          <div className="mb-6" data-id="element-545">
            <div className="h-12 rounded-lg overflow-hidden flex" data-id="element-546">
              <div style={{
              width: `${positivePercent}%`,
              backgroundColor: '#639754'
            }} className="flex items-center justify-center" data-id="element-547">
                {positivePercent > 10 && <span className="text-white text-sm font-semibold" data-id="element-548">
                    {positivePercent}%
                  </span>}
              </div>
              <div style={{
              width: `${neutralPercent}%`,
              backgroundColor: '#FFF000'
            }} className="flex items-center justify-center" data-id="element-549">
                {neutralPercent > 10 && <span className="text-gray-800 text-sm font-semibold" data-id="element-550">
                    {neutralPercent}%
                  </span>}
              </div>
              <div style={{
              width: `${negativePercent}%`,
              backgroundColor: '#E60A0A'
            }} className="flex items-center justify-center" data-id="element-551">
                {negativePercent > 10 && <span className="text-white text-sm font-semibold" data-id="element-552">
                    {negativePercent}%
                  </span>}
              </div>
            </div>
          </div>
          <div className="space-y-3" data-id="element-553">
            <div className="flex items-center justify-between p-3 rounded-lg" style={{
            backgroundColor: '#e8f0e5'
          }} data-id="element-554">
              <div className="flex items-center gap-3" data-id="element-555">
                <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{
                backgroundColor: '#639754'
              }} data-id="element-556">
                  <ThumbsUp size={18} className="text-white" data-id="element-557" />
                </div>
                <div data-id="element-558">
                  <div className="font-semibold text-gray-900" data-id="element-559">Positiv</div>
                  <div className="text-xs text-gray-500" data-id="element-560">Zufriedene Kunden</div>
                </div>
              </div>
              <div className="text-right" data-id="element-561">
                <div className="text-2xl font-bold" style={{
                color: '#639754'
              }} data-id="element-562">
                  {currentData.positive.toLocaleString('de-DE')}
                </div>
                <div className="text-xs text-gray-500" data-id="element-563">
                  {positivePercent}% aller Bewertungen
                </div>
              </div>
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg" style={{
            backgroundColor: '#fffcdb'
          }} data-id="element-564">
              <div className="flex items-center gap-3" data-id="element-565">
                <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{
                backgroundColor: '#FFF000'
              }} data-id="element-566">
                  <Minus size={18} className="text-gray-800" data-id="element-567" />
                </div>
                <div data-id="element-568">
                  <div className="font-semibold text-gray-900" data-id="element-569">Neutral</div>
                  <div className="text-xs text-gray-500" data-id="element-570">
                    Gemischte Meinungen
                  </div>
                </div>
              </div>
              <div className="text-right" data-id="element-571">
                <div className="text-2xl font-bold" style={{
                color: '#b39500'
              }} data-id="element-572">
                  {currentData.neutral.toLocaleString('de-DE')}
                </div>
                <div className="text-xs text-gray-500" data-id="element-573">
                  {neutralPercent}% aller Bewertungen
                </div>
              </div>
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg" style={{
            backgroundColor: '#fce8e7'
          }} data-id="element-574">
              <div className="flex items-center gap-3" data-id="element-575">
                <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{
                backgroundColor: '#E60A0A'
              }} data-id="element-576">
                  <ThumbsDown size={18} className="text-white" data-id="element-577" />
                </div>
                <div data-id="element-578">
                  <div className="font-semibold text-gray-900" data-id="element-579">Negativ</div>
                  <div className="text-xs text-gray-500" data-id="element-580">
                    Unzufriedene Kunden
                  </div>
                </div>
              </div>
              <div className="text-right" data-id="element-581">
                <div className="text-2xl font-bold" style={{
                color: '#E60A0A'
              }} data-id="element-582">
                  {currentData.negative.toLocaleString('de-DE')}
                </div>
                <div className="text-xs text-gray-500" data-id="element-583">
                  {negativePercent}% aller Bewertungen
                </div>
              </div>
            </div>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-584">
        {currentTopics.map((topic, index) => {
        let posPct, neuPct, negPct;
        if (topic.sentiment === 'positive') {
          posPct = 0.68;
          neuPct = 0.12;
          negPct = 0.2;
        } else if (topic.sentiment === 'negative') {
          posPct = 0.2;
          neuPct = 0.1;
          negPct = 0.7;
        } else {
          posPct = 0.3;
          neuPct = 0.4;
          negPct = 0.3;
        }
        const positiveCount = Math.round(topic.size * posPct);
        const neutralCount = Math.round(topic.size * neuPct);
        const negativeCount = topic.size - positiveCount - neutralCount;
        return <Card key={index} className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => handleTopicClick(topic)} data-id="element-585">
              <div className="p-4" data-id="element-586">
                <div className="flex items-center justify-between mb-3" data-id="element-587">
                  <span className="text-sm text-gray-600" data-id="element-588">
                    Erwähnung:{' '}
                    <span className="font-semibold text-gray-900" data-id="element-589">
                      {topic.name}
                    </span>
                  </span>
                  <span className="text-sm font-semibold text-gray-900" data-id="element-590">
                    {topic.size.toLocaleString('de-DE')}
                  </span>
                </div>

                <div className="w-full h-3 flex rounded-full overflow-hidden mb-2" data-id="element-591">
                  <div onClick={e => {
                e.stopPropagation();
                handleTopicClick(topic, 'positive');
              }} className="cursor-pointer hover:opacity-80 transition-opacity" title="Positive Kommentare anzeigen" style={{
                width: `${posPct * 100}%`,
                backgroundColor: '#639754'
              }} data-id="element-592" />
                  <div onClick={e => {
                e.stopPropagation();
                handleTopicClick(topic, 'neutral');
              }} className="cursor-pointer hover:opacity-80 transition-opacity" title="Neutrale Kommentare anzeigen" style={{
                width: `${neuPct * 100}%`,
                backgroundColor: '#FFF000'
              }} data-id="element-593" />
                  <div onClick={e => {
                e.stopPropagation();
                handleTopicClick(topic, 'negative');
              }} className="cursor-pointer hover:opacity-80 transition-opacity" title="Negative Kommentare anzeigen" style={{
                width: `${negPct * 100}%`,
                backgroundColor: '#E60A0A'
              }} data-id="element-594" />
                </div>

                <div className="flex items-center justify-between text-xs text-gray-600 mt-2" data-id="element-595">
                  <div className="flex items-center gap-1.5" data-id="element-596">
                    <div className="w-2 h-2 rounded-full bg-[#639754]" data-id="element-597" />
                    {positiveCount.toLocaleString('de-DE')}
                  </div>
                  <div className="flex items-center gap-1.5" data-id="element-598">
                    <div className="w-2 h-2 rounded-full bg-[#FFF000]" data-id="element-599" />
                    {neutralCount.toLocaleString('de-DE')}
                  </div>
                  <div className="flex items-center gap-1.5" data-id="element-600">
                    <div className="w-2 h-2 rounded-full bg-[#E60A0A]" data-id="element-601" />
                    {negativeCount.toLocaleString('de-DE')}
                  </div>
                </div>
              </div>
            </Card>;
      })}
      </div>

      <Card data-id="element-602">
        <div className="p-6" data-id="element-603">
          <div className="flex items-center justify-between mb-6" data-id="element-604">
            <div data-id="element-605">
              <h3 className="text-lg font-semibold text-gray-900" data-id="element-606">
                Reputationsentwicklung
              </h3>
              <p className="text-sm text-gray-500" data-id="element-607">
                Sentiment-Verteilung der letzten 12 Monate (in %)
              </p>
            </div>
            <div className="flex items-center gap-5 text-sm" data-id="element-608">
              <div className="flex items-center gap-2" data-id="element-609">
                <div className="w-3 h-3 rounded-full" style={{
                backgroundColor: '#639754'
              }} data-id="element-610"></div>
                <span className="text-gray-600" data-id="element-611">Positiv</span>
              </div>
              <div className="flex items-center gap-2" data-id="element-612">
                <div className="w-3 h-3 rounded-full" style={{
                backgroundColor: '#FFF000'
              }} data-id="element-613"></div>
                <span className="text-gray-600" data-id="element-614">Neutral</span>
              </div>
              <div className="flex items-center gap-2" data-id="element-615">
                <div className="w-3 h-3 rounded-full" style={{
                backgroundColor: '#E60A0A'
              }} data-id="element-616"></div>
                <span className="text-gray-600" data-id="element-617">Negativ</span>
              </div>
            </div>
          </div>
          <div className="h-72" data-id="element-618">
            <ResponsiveContainer width="100%" height="100%" data-id="element-619">
              <LineChart data={currentTrend} data-id="element-620">
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} data-id="element-621" />
                <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{
                fontSize: 11,
                fill: '#9ca3af'
              }} data-id="element-622" />
                <YAxis domain={[0, 100]} axisLine={false} tickLine={false} tick={{
                fontSize: 11,
                fill: '#9ca3af'
              }} tickFormatter={value => `${value}%`} data-id="element-623" />
                <Tooltip contentStyle={{
                borderRadius: '8px',
                border: 'none',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                fontSize: '12px'
              }} formatter={(value: number, name: string) => {
                const labels: Record<string, string> = {
                  positivePercent: 'Positiv',
                  neutralPercent: 'Neutral',
                  negativePercent: 'Negativ'
                };
                return [`${value}%`, labels[name] || name];
              }} data-id="element-624" />
                <Line type="monotone" dataKey="positivePercent" stroke="#639754" strokeWidth={3} dot={{
                fill: '#639754',
                strokeWidth: 2,
                r: 4
              }} activeDot={{
                r: 6,
                fill: '#639754',
                stroke: '#fff',
                strokeWidth: 2
              }} isAnimationActive={false} data-id="element-625" />
                <Line type="monotone" dataKey="neutralPercent" stroke="#FFF000" strokeWidth={3} dot={{
                fill: '#FFF000',
                strokeWidth: 2,
                r: 4
              }} activeDot={{
                r: 6,
                fill: '#FFF000',
                stroke: '#fff',
                strokeWidth: 2
              }} isAnimationActive={false} data-id="element-626" />
                <Line type="monotone" dataKey="negativePercent" stroke="#E60A0A" strokeWidth={3} dot={{
                fill: '#E60A0A',
                strokeWidth: 2,
                r: 4
              }} activeDot={{
                r: 6,
                fill: '#E60A0A',
                stroke: '#fff',
                strokeWidth: 2
              }} isAnimationActive={false} data-id="element-627" />
              </LineChart>
            </ResponsiveContainer>
          </div>
          {currentTrend.length > 1 && <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between flex-wrap gap-4" data-id="element-628">
              <div className="flex items-center gap-6" data-id="element-629">
                <div className="flex items-center gap-2" data-id="element-630">
                  <TrendingUp size={16} style={{
                color: '#639754'
              }} data-id="element-631" />
                  <span className="text-sm text-gray-600" data-id="element-632">
                    Positiv:{' '}
                    <span className="font-semibold" style={{
                  color: '#639754'
                }} data-id="element-633">
                      {currentTrend[currentTrend.length - 1].positivePercent}%
                    </span>
                  </span>
                </div>
                <div className="flex items-center gap-2" data-id="element-634">
                  <Minus size={16} style={{
                color: '#b39500'
              }} data-id="element-635" />
                  <span className="text-sm text-gray-600" data-id="element-636">
                    Neutral:{' '}
                    <span className="font-semibold" style={{
                  color: '#b39500'
                }} data-id="element-637">
                      {currentTrend[currentTrend.length - 1].neutralPercent}%
                    </span>
                  </span>
                </div>
                <div className="flex items-center gap-2" data-id="element-638">
                  <TrendingDown size={16} style={{
                color: '#E60A0A'
              }} data-id="element-639" />
                  <span className="text-sm text-gray-600" data-id="element-640">
                    Negativ:{' '}
                    <span className="font-semibold" style={{
                  color: '#E60A0A'
                }} data-id="element-641">
                      {currentTrend[currentTrend.length - 1].negativePercent}%
                    </span>
                  </span>
                </div>
              </div>
              <div className="text-sm text-gray-500" data-id="element-642">
                Veränderung Positiv:{' '}
                <span className="font-semibold" style={{
              color: '#639754'
            }} data-id="element-643">
                  +
                  {currentTrend[currentTrend.length - 1].positivePercent - currentTrend[0].positivePercent}{' '}
                  Pp.
                </span>
              </div>
            </div>}
        </div>
      </Card>

      <AnimatePresence data-id="element-644">
        {isDialogOpen && selectedTopic && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-black/50 flex items-center justify-center z-[600] p-4" onClick={() => setIsDialogOpen(false)} data-id="element-645">
            <motion.div initial={{
          scale: 0.95,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} exit={{
          scale: 0.95,
          opacity: 0
        }} className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-646">
              <div className="p-6 border-b border-gray-200 bg-gray-50" data-id="element-647">
                <div className="flex items-center justify-between" data-id="element-648">
                  <div data-id="element-649">
                    <div className="flex items-center gap-3" data-id="element-650">
                      <h3 className="text-lg font-semibold text-gray-900" data-id="element-651">
                        {selectedTopic.name}
                      </h3>
                      {selectedSentimentFilter ? <Badge variant={selectedSentimentFilter === 'positive' ? 'success' : selectedSentimentFilter === 'negative' ? 'danger' : 'warning'} data-id="element-652">
                          {sentimentLabels[selectedSentimentFilter]}
                        </Badge> : <Badge variant={selectedTopic.sentiment === 'positive' ? 'success' : selectedTopic.sentiment === 'negative' ? 'danger' : 'warning'} data-id="element-653">
                          {sentimentLabels[selectedTopic.sentiment]}
                        </Badge>}
                    </div>
                    <p className="text-sm text-gray-500 mt-1" data-id="element-654">
                      {selectedTopic.size.toLocaleString('de-DE')} Erwähnungen
                      in Kundenbewertungen
                    </p>
                    {selectedSentimentFilter && <div className="flex gap-2 mt-3" data-id="element-655">
                        {(['positive', 'neutral', 'negative'] as const).map(s => <button key={s} onClick={() => setSelectedSentimentFilter(s)} className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${selectedSentimentFilter === s ? 'text-white shadow-sm' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} style={selectedSentimentFilter === s ? {
                    backgroundColor: sentimentColors[s]
                  } : undefined} data-id="element-656">
                              {sentimentLabels[s]}
                            </button>)}
                      </div>}
                  </div>
                  <button onClick={() => setIsDialogOpen(false)} className="p-2 hover:bg-gray-200 rounded-full transition-colors" data-id="element-657">
                    <X size={20} data-id="element-658" />
                  </button>
                </div>
              </div>
              <div className="p-6 overflow-y-auto max-h-[60vh]" data-id="element-659">
                <div className="space-y-4" data-id="element-660">
                  {getFilteredComments().length === 0 && <div className="text-center py-8 text-gray-400" data-id="element-661">
                      <MessageSquare size={32} className="mx-auto mb-3 opacity-50" data-id="element-662" />
                      <p className="text-sm" data-id="element-663">
                        Keine Kommentare für{' '}
                        <span className="font-medium" style={{
                    color: sentimentColors[selectedSentimentFilter || 'neutral']
                  }} data-id="element-664">
                          {sentimentLabels[selectedSentimentFilter || 'neutral']}
                        </span>{' '}
                        verfügbar.
                      </p>
                    </div>}
                  {getFilteredComments().map((comment, index) => <motion.div key={index} initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: index * 0.05
              }} className="p-4 bg-gray-50 rounded-lg border border-gray-100" data-id="element-665">
                      <div className="flex items-start justify-between gap-4 mb-2" data-id="element-666">
                        <div className="flex items-center gap-2" data-id="element-667">
                          <MapPin size={14} className="text-lidl-blue" data-id="element-668" />
                          <span className="text-sm font-medium text-gray-900" data-id="element-669">
                            {comment.market}
                          </span>
                        </div>
                        <div className="flex items-center gap-2" data-id="element-670">
                          <div className="flex" data-id="element-671">
                            {[...Array(5)].map((_, i) => <span key={i} className={`text-sm ${i < comment.rating ? 'text-yellow-400' : 'text-gray-200'}`} data-id="element-672">
                                ★
                              </span>)}
                          </div>
                          <span className="text-xs text-gray-400 flex items-center gap-1" data-id="element-673">
                            <Calendar size={12} data-id="element-674" />
                            {new Date(comment.date).toLocaleDateString('de-DE')}
                          </span>
                        </div>
                      </div>
                      <p className="text-sm text-gray-700 italic" data-id="element-675">
                        "{comment.text}"
                      </p>
                    </motion.div>)}
                </div>
              </div>
              <div className="p-4 border-t border-gray-200 bg-gray-50 flex justify-end" data-id="element-676">
                <Button variant="primary" onClick={() => setIsDialogOpen(false)} data-id="element-677">
                  Schließen
                </Button>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}